import { detectStage, shouldShowStageBadge } from "@/lib/stage"
import { cn } from "@/lib/utils"

interface StageBadgeProps {
  /** Tamanho da fonte; default 10px (xs reduzido). */
  size?: "xs" | "sm"
  /** Classes adicionais para customizar o posicionamento. */
  className?: string
}

/**
 * Badge identificador de stage exibido ao lado do logo nos ambientes de teste:
 *   - alfa     → vermelho
 *   - beta     → amarelo
 *   - local    → cinza (dev local, nunca aparece em produção)
 *   - preview  → azul (CF Pages preview de feature branch)
 *   - producao → não renderiza nada
 *
 * Decisão UX (CEO 2026-04-30): clientes finais que entram em creditos.invenia.com.br
 * não devem ver nenhuma marca de stage. Apenas as URLs alfa/beta/local/preview têm
 * a tag, alertando o usuário de que está em ambiente não-oficial.
 */
export function StageBadge({ size = "xs", className }: StageBadgeProps) {
  const stage = detectStage()
  if (!shouldShowStageBadge(stage)) return null

  const config: Record<typeof stage, { label: string; classes: string }> = {
    alfa: {
      label: "alfa",
      classes: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30",
    },
    beta: {
      label: "beta",
      classes: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
    },
    local: {
      label: "local",
      classes: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
    },
    preview: {
      label: "preview",
      classes: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
    },
    producao: { label: "", classes: "" },
  }

  const c = config[stage]
  const sizeClasses = size === "sm"
    ? "text-xs px-1.5 py-0.5"
    : "text-[10px] px-1 py-px"

  // SHA curto (7 chars) injetado em build time. Útil pra confirmar que o
  // browser está vendo a versão mais recente em alfa/beta/preview.
  const shortSha = typeof __COMMIT_SHA__ === "string" ? __COMMIT_SHA__.slice(0, 7) : null

  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span
        className={cn(
          "inline-flex items-center rounded font-semibold uppercase tracking-wider border leading-none",
          sizeClasses,
          c.classes,
        )}
        title={`Ambiente: ${c.label}`}
        aria-label={`Ambiente ${c.label}`}
      >
        {c.label}
      </span>
      {shortSha && shortSha !== "dev" && (
        <span
          className={cn(
            "inline-flex items-center rounded border leading-none font-mono",
            sizeClasses,
            "bg-glass-card text-[var(--text-tertiary)] border-glass-border",
          )}
          title={`Build commit: ${__COMMIT_SHA__}`}
          aria-label={`Commit ${shortSha}`}
        >
          {shortSha}
        </span>
      )}
    </span>
  )
}
